import { useGeneratePhotographiesSinglePdf } from "@/api/pdf";
import { Photography } from "@/lib/types";
import { PropsWithChildren, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PrintDrawer from "../print/PrintDrawer";
import { Checkbox } from "../ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import {
  PhotographyPrintSettings,
  simpleTemplateFields,
} from "./photographyPrintSettings";
import i18n, { SupportedLocale } from "@/i18n";

type PhotographyPrintSingleDrawerProps = PropsWithChildren<{
  selectedPhotography: Photography;
}>;

type FormValues = {
  settings: PhotographyPrintSettings;
};

const PhotographyPrintSingleDrawer = ({
  selectedPhotography,
}: PhotographyPrintSingleDrawerProps) => {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    defaultValues: {
      settings: simpleTemplateFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: false,
        }),
        {} as PhotographyPrintSettings,
      ),
    },
  });
  const { mutateAsync: generatePhotographiesPdf, isPending } =
    useGeneratePhotographiesSinglePdf();
  const formId = useId();
  const [previewURL, setPreviewURL] = useState<string | null>(null);

  const onSubmit = async (data: FormValues) => {
    const file = await generatePhotographiesPdf({
      lang: i18n.language as SupportedLocale,
      template: "simple",
      photographyId: selectedPhotography.id,
      settings: data.settings,
    });
    const url = URL.createObjectURL(file);
    setPreviewURL(url);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
        <PrintDrawer
          formId={formId}
          submitButtonProps={{ isLoading: isPending }}
          previewUrl={previewURL}
          setPreviewUrl={setPreviewURL}
          hideText={true}
          iconSize={18}
        >
          <div className="flex flex-col gap-6">
            <div className="space-y-4">
              <FormLabel>{t("printOptions")}</FormLabel>
              {simpleTemplateFields.map((option) => (
                <FormField
                  key={option}
                  control={form.control}
                  name={`settings.${option}`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value as boolean}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel>{t(option)}</FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>
        </PrintDrawer>
      </form>
    </Form>
  );
};

export default PhotographyPrintSingleDrawer;
